import React from 'react';

import { UxContext } from '@client/context/UxContext';
import useLediumPage from '@client/hooks/useLediumPage';
import { usePageStore } from '@client/store/pageStore/context/pageContext';
import i18n from '@client/utils/i18n';
import { RecipeSection } from '@ledium/types';

import RecipeEditorView from './RecipeEditorView';

const RecipeEditor = () => {
    const { handleSnackbar } = React.useContext(UxContext)!;
    const {
        pageState: { formData },
        pageActions
    } = usePageStore();

    const [selectedSection, setSelectedSection] = React.useState<RecipeSection | null>(null);
    const [autoSaveKey, setAutoSaveKey] = React.useState(0);

    const { mainMenu, onSave } = useLediumPage({
        saveDataMap: (saveFormData) => {
            const data = saveFormData ? saveFormData : formData;
            return {
                name: data.name,
                description: data.description,
                sectionList: (data.sectionList as RecipeSection[]).map((section) => ({
                    circadianLength: section.circadianLength,
                    sectionColor: {
                        background: section.sectionColor!.background,
                        text: section.sectionColor!.text
                    },
                    id: section.id,
                    name: section.name,
                    orderPosition: section.orderPosition,
                    repeatNum: section.repeatNum,
                    circadianEvents: section.circadianEvents.map((event) => ({
                        hour: event.hour,
                        minute: event.minute,
                        lightRecipeId: event.lightRecipeId
                    }))
                }))
            };
        }
    });

    function onSaveSection(sectionData: RecipeSection, opts: { recipeSave?: boolean } = {}) {
        sectionData.circadianEvents.sort((a, b) => (+a.hour + a.minute / 60 > +b.hour + b.minute / 60 ? 1 : -1));
        let sectionList = formData.sectionList as RecipeSection[];

        if (sectionData.id) {
            sectionList = sectionList.map((e) => (e.id === sectionData.id ? sectionData : e));
        } else {
            const updatedIds = sectionList.reduce(
                (a, c) => ({
                    orderPosition: c.orderPosition >= a.orderPosition ? c.orderPosition + 1 : a.orderPosition,
                    id: (c.id as unknown as number) >= a.id ? (c.id as unknown as number) + 1 : a.id
                }),
                { orderPosition: 1, id: 1 }
            );
            sectionList.push({ ...sectionData, ...updatedIds } as unknown as RecipeSection);
        }
        const updatedFormData = { ...formData, sectionList };
        pageActions.formData.overrideFormData(updatedFormData);

        if ((updatedFormData as Record<string, unknown>).id && opts.recipeSave !== false) {
            onSave(updatedFormData);
            setAutoSaveKey((k) => k + 1);
        } else {
            handleSnackbar({
                message: i18n('recipe_section_saved_info') as string,
                severity: 'info'
            });
        }

        setSelectedSection(null);
        pageActions.formData.setFormSubmitting(false);
    }

    function onDeleteSection(sectionData: RecipeSection) {
        const sectionList = (formData.sectionList as RecipeSection[]).filter((e) => e.id !== sectionData.id);
        const updatedFormData = { ...formData, sectionList };
        pageActions.formData.overrideFormData(updatedFormData);
        setSelectedSection(null);

        if ((updatedFormData as Record<string, unknown>).id) {
            onSave(updatedFormData);
            setAutoSaveKey((k) => k + 1);
        }
    }

    if (!formData.loaded) {
        return null;
    }

    return (
        <RecipeEditorView
            onSave={onSave}
            onClose={mainMenu}
            selectedSection={selectedSection}
            setSelectedSection={setSelectedSection}
            onSaveSection={onSaveSection}
            onDeleteSection={onDeleteSection}
            autoSaveKey={autoSaveKey}
        />
    );
};

export default RecipeEditor;
